import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, EventEmitter, Output, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EkButtonComponent } from '@beta-foundations/ui-kit/buttons';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../models/user';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EkCardModule } from '@beta-foundations/ui-kit/panel/card';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LogoutPageComponent } from '../logout-page/logout-page.component';
import { DrawerModule } from 'primeng/drawer';
import { Button } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { PageTitleService } from '../../services/page-title.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
    imports: [ CommonModule,
        TranslateModule,
        EkButtonComponent,
        EkCardModule,
        ReactiveFormsModule,
        DrawerModule,
        Button,
        ChipModule ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  providers: [DialogService]
})
export class NavbarComponent implements OnInit, OnDestroy {

  ref: DynamicDialogRef | undefined;

  isAuthenticated: boolean = false;
  userName: string | null = null;
  user: User | null = null;
  @Input()
  showFeedbackButton = false;
  @Output() feedbackClick = new EventEmitter();

  examStatus: 'UNSAVED' | 'BOZZA' | 'COMPLETATO' | null = null;
  showExamStatus: boolean = false;
  private destroy$ = new Subject<void>();

  private router = inject(Router);

  languageForm: FormGroup;
  languages = [
    { label: 'Italiano', value: 'it' },
    { label: 'English', value: 'en' }
  ];

  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private dialogService: DialogService,
    private pageTitleService: PageTitleService
  ) {
    const browserLang = this.translate.getBrowserLang();
    const initialLang = browserLang?.match(/en|it/) ? browserLang : this.translate.currentLang || this.translate.defaultLang || 'it';

    this.languageForm = new FormGroup({
      selectedLanguageForm: new FormControl<string>(initialLang, { nonNullable: true })
    });

    this.languageForm.get('selectedLanguageForm')?.valueChanges.subscribe(newLang => {
      if (newLang && newLang !== this.translate.currentLang) {
        this.translate.use(newLang).subscribe({
          next: () => this.cdr.detectChanges(),
          error: (error) => console.error(`Errore cambio lingua:`, error)
        });
      }
    });

    this.translate.use(initialLang).subscribe({
      error: () => this.languageForm.get('selectedLanguageForm')?.setValue('it')
    });
  }

  showFeedback() {
      this.feedbackClick.emit(true);
  }

  openUserDialog() {
    this.ref = this.dialogService.open(LogoutPageComponent, {
      width: '330px',
      height: '150px',
      data: {
        userName: this.userName,
      },
      modal: true,
      dismissableMask: true,
      style: { right: '7vh', top: '8vh', position: 'fixed', overflow: 'auto' },
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.logout();
      }
    });
  }

  ngOnInit(): void {
    this.loadUserInfo();

    this.pageTitleService.examStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.examStatus = status;
        this.showExamStatus = status !== null;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get languageFormControl(): FormControl<string> {
    return this.languageForm.get('selectedLanguageForm') as FormControl<string>;
  }

  async loadUserInfo(): Promise<void> {
    const userDetails = await firstValueFrom(this.authService.getUserInfo());

    if (userDetails) {
      this.isAuthenticated = true;
      this.user = userDetails;
      this.userName = userDetails.name + ' ' + userDetails.surname;
      console.log('Navbar: User details loaded:', this.user);
    } else {
      this.isAuthenticated = false;
      this.user = null;
      this.userName = null;
      console.warn('Navbar: No user details found or user not authenticated.');
    }
  }

  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.isAuthenticated = false;
      this.userName = null;
      this.router.navigate(['/login']);
    } catch (error) {
      console.log("logout failed:", error);
    }
  }

}
